import { useRef } from "react";
import { Link } from "react-router-dom";
import { useInView } from "framer-motion";
import { ShieldCheck, ArrowRight } from "lucide-react";
import { FadeIn, MaskedLines, Eyebrow } from "@/components/Reveal";
import { useTitle } from "@/hooks/use-title";

const chapters = [
  {
    n: "01",
    title: "Multidisciplinary Expertise",
    copy: "Medical writers, clinicians, biostatisticians, and pharmaceutical scientists collaborate on every engagement. Your project is never seen through a single lens — it is examined from the bench, the bedside, and the editorial desk simultaneously.",
    points: ["Medical Writers", "Clinicians", "Biostatisticians", "Pharma Scientists"],
  },
  {
    n: "02",
    title: "Strict Confidentiality & Data Integrity",
    copy: "Unpublished data and pre-award ideas are your most fragile assets. We operate under non-disclosure by default, restrict access on a need-to-know basis, and treat data integrity as non-negotiable — 100% confidentiality, always.",
    points: ["NDA On Request", "Need-To-Know Access", "Verifiable Data Handling"],
  },
  {
    n: "03",
    title: "Customized Scientific Solutions",
    copy: "No templates, no boilerplate. Every deliverable is scoped against your objective, your audience, and your target journal or regulatory pathway — then built to fit nothing else.",
    points: ["Scoped Per Project", "Audience-First", "Flexible Engagement"],
  },
  {
    n: "04",
    title: "Ethical & Timely Delivery",
    copy: "We work to ICMJE and GPP standards, give honest timelines at scoping, and hit them. Authorship, attribution, and disclosure are handled transparently — because credibility is the product.",
    points: ["ICMJE / GPP Aligned", "Honest Timelines", "Transparent Process"],
  },
  {
    n: "05",
    title: "Journal & Regulatory Mastery",
    copy: "A working command of journal expectations, reviewer psychology, and regulatory frameworks. We position your work where it belongs — and anticipate the questions before they are asked.",
    points: ["Journal Strategy", "Reviewer Responses", "Regulatory Expectations"],
  },
];

const charter = [
  { title: "NDA on request", copy: "Formal non-disclosure before any document changes hands." },
  { title: "Controlled access", copy: "Only the specialists assigned to your project see your data." },
  { title: "Secure handling", copy: "Structured storage and transfer practices for every file." },
  { title: "100% — always", copy: "Confidentiality is a founding principle, not an add-on." },
];

const stats = [
  { value: "06", label: "Core Domains" },
  { value: "100%", label: "Confidential" },
  { value: "05", label: "Lifecycle Stages" },
  { value: "Global", label: "Clientele" },
];

const Chapter = ({ chapter, i }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-35% 0px -35% 0px" });
  return (
    <div
      ref={ref}
      className="grid gap-6 border-t border-slate-200 py-14 sm:py-16 lg:grid-cols-12"
      data-testid={`why-chapter-${i + 1}`}
    >
      <div className="lg:col-span-4">
        <span
          className={`font-serif text-6xl leading-none transition-colors duration-700 sm:text-7xl lg:text-8xl ${
            inView ? "text-coral-400" : "text-slate-200"
          }`}
        >
          {chapter.n}
        </span>
      </div>
      <div className="lg:col-span-8">
        <h3
          className={`font-serif text-2xl tracking-tight transition-colors duration-700 sm:text-3xl ${
            inView ? "text-slate-900" : "text-slate-400"
          }`}
        >
          {chapter.title}
        </h3>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
          {chapter.copy}
        </p>
        <ul className="mt-7 flex flex-wrap gap-2.5">
          {chapter.points.map((p) => (
            <li
              key={p}
              className="rounded-full border border-slate-200 bg-white px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500"
            >
              {p}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default function WhyUs() {
  useTitle("Why Choose Us — THREENNOVATORS LLP");

  return (
    <div data-testid="why-us-page">
      <section className="mx-auto max-w-7xl px-4 pb-10 pt-16 sm:px-6 sm:pt-24 lg:px-8">
        <FadeIn y={12}>
          <Eyebrow>Why Choose Us</Eyebrow>
        </FadeIn>
        <h1
          className="mt-6 max-w-3xl font-serif text-4xl leading-[1.1] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
          data-testid="why-us-headline"
        >
          <MaskedLines
            lines={[
              "The manifesto of a",
              <>
                <span className="italic text-coral-500">credible</span> partner.
              </>,
            ]}
          />
        </h1>
        <FadeIn delay={0.45}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
            Five commitments govern every engagement at THREENNOVATORS LLP.
            They are not marketing lines — they are operating principles.
          </p>
        </FadeIn>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8" data-testid="why-manifesto">
        {chapters.map((c, i) => (
          <Chapter key={c.n} chapter={c} i={i} />
        ))}
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <FadeIn className="overflow-hidden rounded-[2.5rem] bg-slate-950 px-6 py-14 sm:px-12 sm:py-16">
          <div className="flex items-center gap-4">
            <ShieldCheck className="h-8 w-8 text-coral-400" strokeWidth={1.5} />
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-coral-400">
              The Confidentiality Charter
            </p>
          </div>
          <h2 className="mt-6 max-w-2xl font-serif text-2xl leading-tight text-white sm:text-3xl lg:text-4xl">
            Your science stays yours.{" "}
            <span className="italic text-coral-400">Full stop.</span>
          </h2>
          <div className="mt-12 grid gap-px overflow-hidden rounded-2xl bg-white/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="why-charter-grid">
            {charter.map((c) => (
              <div key={c.title} className="bg-slate-950 p-7">
                <h3 className="font-sans text-base font-semibold text-white">{c.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{c.copy}</p>
              </div>
            ))}
          </div>
        </FadeIn>
      </section>

      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-10 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-4 lg:px-8" data-testid="why-stats-strip">
          {stats.map((s, i) => (
            <FadeIn key={s.label} delay={i * 0.08} className="text-center lg:text-left">
              <p className="font-serif text-4xl text-slate-900 sm:text-5xl">{s.value}</p>
              <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                {s.label}
              </p>
            </FadeIn>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <FadeIn className="flex flex-col items-start justify-between gap-8 rounded-[2.5rem] border border-coral-200 bg-coral-50 p-10 sm:p-14 lg:flex-row lg:items-center">
          <div>
            <h2 className="font-serif text-2xl tracking-tight text-slate-900 sm:text-3xl">
              Put the manifesto to the test.
            </h2>
            <p className="mt-3 max-w-lg text-base text-slate-600">
              Bring us your hardest brief — a rejected manuscript, a stalled
              protocol, a thesis on a deadline.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="why-cta-get-in-touch"
            className="group inline-flex items-center gap-2 rounded-full bg-coral-500 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-coral-600"
          >
            Get in Touch
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeIn>
      </section>
    </div>
  );
}
